import { apiGet, apiPost, apiPatch } from "../lib/api-client";

export type FeedbackType = "bug" | "feature" | "data" | "other";
export type FeedbackStatus = "new" | "archived";

export interface FeedbackSubmission {
  type: FeedbackType;
  title: string;
  body: string;
  details?: Record<string, unknown>;
  context?: Record<string, unknown>;
  photos?: string[];
}

export type FeedbackReplyRole = "admin" | "user";

/** One message on a feedback thread (the original submission is not a reply). */
export interface FeedbackReply {
  id: string;
  authorRole: FeedbackReplyRole;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface MyFeedbackItem {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  status: FeedbackStatus;
  lastRepliedAt: string | null;
  lastUserRepliedAt: string | null;
  replyCount: number;
  /** An admin reply newer than anything the user has written on the thread. */
  hasUnread: boolean;
  createdAt: string;
}

export interface MyFeedbackDetail {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  details: Record<string, unknown> | null;
  photoUrls: string[];
  status: FeedbackStatus;
  lastRepliedAt: string | null;
  lastUserRepliedAt: string | null;
  createdAt: string;
  replies: FeedbackReply[];
}

/** Admin rows carry what the API decided about the email at send time. */
export type ReplyEmailStatus = "queued" | "skipped_placeholder" | "failed_to_queue";

export interface AdminFeedbackReply extends FeedbackReply {
  emailStatus: ReplyEmailStatus | null;
}

/** How a reply reaches the submitter: real email, in-app only (placeholder address), or nobody (account deleted). */
export type SubmitterReachable = "email" | "in_app" | "none";

export interface AdminFeedbackItem {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  details: Record<string, unknown> | null;
  context: Record<string, unknown> | null;
  photoUrls: string[];
  status: FeedbackStatus;
  linearUrl: string | null;
  syncedAt: string | null;
  lastRepliedAt: string | null;
  lastUserRepliedAt: string | null;
  awaitingReply: boolean;
  createdAt: string;
  submitterUserId: string | null;
  submitterName: string | null;
  submitterEmail: string | null;
  submitterReachable: SubmitterReachable;
  replies: AdminFeedbackReply[];
}

export function submitFeedback(input: FeedbackSubmission) {
  return apiPost<{ feedback: { id: string } }>("/feedback", input);
}

export function getFeedbackPhotoUploadUrl(
  contentType: string,
): Promise<{ uploadUrl: string; key: string; publicUrl: string }> {
  return apiPost("/feedback/photos/upload-url", { contentType });
}

export function fetchMyFeedback() {
  return apiGet<{ feedback: MyFeedbackItem[] }>("/feedback/mine");
}

export function fetchMyFeedbackDetail(id: string) {
  return apiGet<{ feedback: MyFeedbackDetail }>(`/feedback/${id}`);
}

export function replyToMyFeedback(id: string, body: string) {
  return apiPost<{ reply: FeedbackReply }>(`/feedback/${id}/replies`, { body });
}

export function adminReplyToFeedback(id: string, body: string) {
  return apiPost<{ reply: AdminFeedbackReply }>(`/admin/feedback/${id}/replies`, { body });
}

export function fetchAdminFeedback(filters: { status?: string; type?: string } = {}) {
  const q = new URLSearchParams();
  if (filters.status) q.set("status", filters.status);
  if (filters.type) q.set("type", filters.type);
  const query = q.toString();
  return apiGet<{ feedback: AdminFeedbackItem[] }>(`/admin/feedback${query ? `?${query}` : ""}`);
}

export function updateFeedbackStatus(id: string, status: FeedbackStatus) {
  return apiPatch<{ feedback: AdminFeedbackItem }>(`/admin/feedback/${id}`, { status });
}
